<template>
  <teleport to="body">
    <template v-if="isDesktop">
      <!-- FAB Button -->
      <button
        type="button"
        class="fixed bottom-6 right-6 z-[110] w-12 h-12 rounded-full bg-[#1980e6] text-white shadow-lg hover:bg-[#166ec5] transition-all flex items-center justify-center"
        @click="isOpen = !isOpen"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span
          v-if="pendingCount > 0"
          class="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#f59e0b] text-[10px] font-bold flex items-center justify-center"
        >
          {{ pendingCount > 9 ? '9+' : pendingCount }}
        </span>
      </button>

      <!-- Backdrop -->
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="isOpen" class="fixed inset-0 z-[109] bg-black/20" @click="isOpen = false" />
      </Transition>

      <!-- Drawer Panel -->
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="isOpen"
          class="fixed top-0 right-0 h-full w-[360px] z-[110] bg-white border-l border-gray-200 shadow-2xl flex flex-col"
        >
          <!-- Drawer Header -->
          <div
            class="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0"
          >
            <span class="text-sm font-semibold text-[#0e141b]">Recent Swaps</span>
            <button
              type="button"
              class="w-7 h-7 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-[#0e141b] transition-colors flex items-center justify-center text-sm"
              @click="isOpen = false"
            >
              ×
            </button>
          </div>

          <!-- Drawer Content -->
          <div class="flex-1 overflow-y-auto">
            <RecentSwapsList
              ref="swapListRef"
              :swaps="swaps"
              :is-loading="isLoading"
              :continuation-states="continuationStates"
              @resume-ton-leg="handleResumeTonLeg"
            />
          </div>
        </div>
      </Transition>
    </template>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRef } from 'vue'
import { refreshRecentSwaps, useRecentSwaps, type RecentSwap } from './use-recent-swaps'
import { useRecentSwapContinuation } from './use-recent-swap-continuation'
import RecentSwapsList from './RecentSwapsList.vue'

interface SwapListRef {
  resetResuming: () => void
}

const props = defineProps<{
  walletAddress?: string
}>()

const swapListRef = ref<SwapListRef | null>(null)

const handleResumeTonLeg = async (swap: RecentSwap) => {
  try {
    await resume(swap)
  } catch (error) {
    if (import.meta.env.DEV) console.error('[RecentSwapsDrawer] Resume failed:', error)
  } finally {
    refreshRecentSwaps()
    swapListRef.value?.resetResuming()
  }
}

const isOpen = ref(false)
const isDesktop = ref(false)

const mediaQuery = typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)') : null

const updateDesktop = () => {
  isDesktop.value = mediaQuery?.matches ?? false
}

onMounted(() => {
  updateDesktop()
  mediaQuery?.addEventListener('change', updateDesktop)
})

onUnmounted(() => {
  mediaQuery?.removeEventListener('change', updateDesktop)
})

const walletAddress = toRef(props, 'walletAddress')
const { swaps, isLoading } = useRecentSwaps()
const historyVisible = computed(() => isDesktop.value && isOpen.value)
const { continuationStates, resume } = useRecentSwapContinuation(walletAddress, historyVisible)

const pendingCount = computed(() => {
  return swaps.value.filter(
    (s) => s.status === 'pending' || s.status === 'bridging' || s.status === 'swapping'
  ).length
})
</script>
